import React, { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, Skull } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { LoadingSpinner } from '../../ui/Loading';
import { useAgentTerminatePreview, useTerminateCdnAgent } from '../../../hooks/useApi';

const TERMINATE_CONFIRM_WORD = 'terminate';

type AgentTarget = {
  id: string;
  email: string;
  name?: string | null;
};

type Props = {
  agent: AgentTarget | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

const TerminateAgentModal: React.FC<Props> = ({ agent, isOpen, onClose, onSuccess }) => {
  const [confirmText, setConfirmText] = useState('');
  const previewQuery = useAgentTerminatePreview(agent?.id, isOpen && !!agent);
  const terminateMutation = useTerminateCdnAgent();

  useEffect(() => {
    if (!isOpen) setConfirmText('');
  }, [isOpen]);

  if (!isOpen || !agent) return null;

  const preview = previewQuery.data?.data;
  const confirmPhraseOk = confirmText.trim().toLowerCase() === TERMINATE_CONFIRM_WORD;
  const canSubmit =
    preview?.canTerminate === true && confirmPhraseOk && !terminateMutation.isLoading && !previewQuery.isLoading;

  const handleClose = () => {
    if (terminateMutation.isLoading) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      await terminateMutation.mutateAsync(agent.id);
      onSuccess?.();
      onClose();
    } catch {
      // toast via hook
    }
  };

  const displayLabel = agent.email || agent.name || agent.id;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="terminate-agent-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <Card className="w-full max-w-md border-destructive/50 shadow-lg" onClick={(e) => e.stopPropagation()}>
        <CardHeader className="space-y-1">
          <CardTitle id="terminate-agent-modal-title" className="text-lg flex items-center gap-2 text-destructive">
            <Skull className="h-5 w-5 shrink-0" aria-hidden />
            Terminate agent?
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            className="rounded-md border border-destructive/50 bg-red-50 dark:bg-red-950/40 p-3 text-sm text-red-950 dark:text-red-100"
            role="alert"
          >
            <p className="font-semibold">Critical — irreversible</p>
            <p className="mt-2 text-red-900/90 dark:text-red-100/90">
              Terminating <span className="font-medium">{displayLabel}</span> ends the agent permanently and freezes
              the agent CDN wallet. This cannot be undone.
            </p>
          </div>

          {previewQuery.isLoading ? (
            <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground">
              <LoadingSpinner className="h-4 w-4" />
              Checking preconditions…
            </div>
          ) : previewQuery.isError ? (
            <p className="text-sm text-destructive">Could not load termination checks. Try again.</p>
          ) : preview && !preview.canTerminate ? (
            <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm dark:border-amber-900 dark:bg-amber-950/40">
              <p className="flex items-center gap-2 font-medium text-amber-950 dark:text-amber-100">
                <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
                Termination blocked
              </p>
              <ul className="list-disc space-y-1 pl-5 text-amber-900/90 dark:text-amber-100/90">
                {preview.blockers.map((b) => (
                  <li key={b.id}>{b.message}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {preview?.canTerminate ? (
            <div className="space-y-2">
              <label htmlFor="confirm-terminate-agent-modal" className="text-sm font-medium">
                Type <span className="font-mono text-foreground">{TERMINATE_CONFIRM_WORD}</span> to confirm
              </label>
              <Input
                id="confirm-terminate-agent-modal"
                autoComplete="off"
                value={confirmText}
                onChange={(ev) => setConfirmText(ev.target.value)}
                placeholder={TERMINATE_CONFIRM_WORD}
                className="font-mono"
                disabled={terminateMutation.isLoading}
              />
            </div>
          ) : null}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={handleClose} disabled={terminateMutation.isLoading}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => void handleSubmit()}
              disabled={!canSubmit}
            >
              {terminateMutation.isLoading ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
                  Working…
                </>
              ) : (
                'Terminate agent'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TerminateAgentModal;
